// Angular Imports
import { BrowserModule } from "@angular/platform-browser";
import { APP_INITIALIZER, Injector, NgModule, isDevMode, NgZone } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { PagesModule } from "./pages/pages.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule } from "@angular/forms";
import { AppConfigModule } from "./appconfig/appconfig.module";
import { Idle, IdleExpiry, LocalStorageExpiry, LocalStorage } from "@ng-idle/core"; // Import Idle service and dependencies
// import { NgIdleKeepaliveModule } from "@ng-idle/keepalive"; // Not compatible with Angular 16 - keepalive functionality removed
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
// import { MomentModule } from 'ngx-moment';

// UI Libraries
import { MdbCompatModule } from './mdb-compat';

// UI Components
import { AppComponent } from "./app.component";
import { loadBrandConfig } from "./core/brand-config/brand-config.loader";
import { NavbarComponent } from "./navbar/navbar.component";
import { FooterComponent } from "./footer/footer.component";


// Sandboxes
import { ModalComponent } from "./modal/modal.component";

// Modal Price Component
import { CreditCardDirectivesModule } from "angular-cc-library";

// Google Places - using custom directive instead
// import { GooglePlaceModule } from "ngx-google-places-autocomplete"; // Not compatible with Angular 16 - replaced with custom directive

// Core Module - include the providers to ensure one instance
// of given service is created across entire application
// (no declarations). Imported in route module only.
import { CoreModule } from "./core/core.module";
import { SharedModule } from "./shared/shared.module";
import { ServiceWorkerModule, SwRegistrationOptions } from "@angular/service-worker";
import { environment } from "../environments/environment";
import { MyAccountVerifyComponent } from "./my-account/my-account-verify/my-account-verify.component";
import { EmployeeSsoComponent } from "./employee-sso/employee-sso.component";
import { InlineLoaderComponent } from "./inline-loader/inline-loader.component";
import { HttpClient, HTTP_INTERCEPTORS } from "@angular/common/http";
import { JwtInterceptor } from "./_helpers/jwt.interceptor";
import { PreferenceComponent } from "./preference/preference.component";
import { ModalDisclaimerComponent } from "./modal-disclaimer/modal-disclaimer.component";
import { ModalBrowserWarningComponent } from "./modal-browser-warning/modal-browser-warning.component";
import { ModalTimeoutComponent } from "./modal-timeout/modal-timeout.component";
import { CartComponent } from "./navbar/cart/cart.component";
import { PhoneModalComponent } from "./navbar/phone-modal/phone-modal.component";
import { DesktopNavComponent } from "./navbar/desktop-nav/desktop-nav.component";
import { DisclaimerComponent } from "./footer/disclaimer/disclaimer.component";
import { QuestionsComponent } from "./footer/questions/questions.component";
import { ContactUsComponent } from "./footer/contact-us/contact-us.component";
import { MyAccountVerifyAccountErrorMessagesComponent } from "./my-account/my-account-verify/my-account-verify-account-error-messages/my-account-verify-account-error-messages.component";
import { MyAccountVerifyEmailComponent } from "./my-account/my-account-verify/my-account-verify-email/my-account-verify-email.component";
import { MyAccountVerifyPhoneComponent } from "./my-account/my-account-verify/my-account-verify-phone/my-account-verify-phone.component";
import { MyAccountVerifyCodeComponent } from "./my-account/my-account-verify/my-account-verify-code/my-account-verify-code.component";
import { PrismicService } from "./_services/prismic.service";
import { ModalVinComponent } from "./modal-vin/modal-vin.component";
import { ModalInfoComponent } from "./modal-info/modal-info.component";
import { CookieAcceptanceComponent } from "./cookie-acceptance/cookie-acceptance.component";
import { CheckoutFooterComponent } from "./footer/checkout/checkout.component";

import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { ModalTechnicalDifficultyComponent } from "./modal-technical-difficulty-error/modal-technical-difficulty-error.component";
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular'
import { Router } from "@angular/router";
import myOktaConfig  from './okta-config'
// import { NgwWowModule } from 'ngx-wow'; // Not compatible with Angular 16
import { ModalInterstitialComponent } from "./modal-interstitial-popup/modal-Interstitial-popup.component";
import { CurrencyPipe } from "@angular/common";
import { ViewContractComponent } from './view-contract/view-contract.component';
import { ModalVehicleInspectionComponent } from "./modal-vehicle-inspection-s3/modal-vehicle-inspection.component";
import { NgxFileDropModule } from 'ngx-file-drop';
import { ModalSwitchScreenComponent } from "./modal-switch-screen/modal-switch-screen.component";
import { ModalDeleteDocumentComponent } from "./modal-delete-document/modal-delete-document.component";
import { ModalMarketingEmailComponent } from "./modal-marketing-email/modal-marketing-email.component";
import { SessionStorageDataComponent } from './session-storage-data/session-storage-data.component';
import brandInfo from '../environments/brand.json';
import { MatDialogModalComponent } from './mat-dialog-modal/mat-dialog-modal.component'
import { ModalDripsComponent } from "./modal-drips/modal-drips.component";
import { ViewCancellationComponent } from './view-cancellation/view-cancellation.component';

const oktaConfig = Object.assign({
  onAuthRequired: (oktaAuth: any, injector: Injector) => {
    const router = injector.get(Router)
    router.navigate(['/verify-account'])
  }
}, myOktaConfig.oidc)

@NgModule({
    declarations: [
        // AppComponent, NavbarComponent, FooterComponent removed - now standalone
        ModalComponent,
        PreferenceComponent,
        MyAccountVerifyComponent,
        MyAccountVerifyAccountErrorMessagesComponent,
        MyAccountVerifyEmailComponent,
        MyAccountVerifyPhoneComponent,
        MyAccountVerifyCodeComponent,
        ModalBrowserWarningComponent,
        ModalTimeoutComponent,
        CartComponent,
        // PhoneModalComponent, DesktopNavComponent, DisclaimerComponent, QuestionsComponent, ContactUsComponent, CheckoutFooterComponent - now standalone
        ModalInfoComponent,
        ModalDripsComponent,
        ModalVehicleInspectionComponent,
        ModalSwitchScreenComponent,
        ModalDeleteDocumentComponent,
        ModalInterstitialComponent,
        CookieAcceptanceComponent,
        ModalVinComponent,
        ModalTechnicalDifficultyComponent,
        ViewContractComponent,
        ModalMarketingEmailComponent,
        SessionStorageDataComponent,
        MatDialogModalComponent,
        ViewCancellationComponent,
        EmployeeSsoComponent,
        ],
    imports: [
        BrowserModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        FormsModule,
        BrowserAnimationsModule,
        SharedModule,
        CoreModule,
        // GooglePlaceModule, // Removed - not compatible with Angular 16. Using custom directive instead
        AppConfigModule,
        MdbCompatModule.forRoot(),
        CreditCardDirectivesModule,
        // The root routes render ProductHomeComponent directly for ':pageurl' and
        // ':parenturl/:pageurl' — every Prismic page — so the module declaring it
        // has to be part of the eager graph. Nothing else loads it on those routes,
        // and the slice components it renders get their pipes and directives from
        // scope calls the compiler emits into PagesModule and SlicesModule. Without
        // this the app depends on the bundler happening to place those files in a
        // chunk that loads anyway: it does in a production build, but not under
        // ng serve, where the slices render with no pipes (NG0302) and no
        // slick-carousel export (NG0301).
        PagesModule,
        AppRoutingModule,
        // NgIdleKeepaliveModule.forRoot(), // Removed - not compatible with Angular 16
        // NgwWowModule, // Removed - not compatible with Angular 16
        StoreDevtoolsModule.instrument({
          name: 'DevTools & Debugging in NgRx',
          maxAge: 25,
          logOnly: environment.production, // Restrict extension to log-only mode
        }),
        // MomentModule,
        // OktaAuthModule,
       NgxFileDropModule
    ],
    providers: [
        // Provide Idle service and dependencies manually (module not compatible with Angular 16)
        {
            provide: LocalStorage,
            useFactory: () => new LocalStorage()
        },
        {
            provide: LocalStorageExpiry,
            useFactory: (localStorage: LocalStorage) => new LocalStorageExpiry(localStorage),
            deps: [LocalStorage]
        },
        { provide: IdleExpiry, useExisting: LocalStorageExpiry },
        {
            provide: Idle,
            useFactory: (expiry: IdleExpiry, zone: NgZone) => new Idle(expiry, zone),
            deps: [IdleExpiry, NgZone]
        },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: PrismicService.initApp,
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: loadBrandConfig,
            multi: true,
        },
        {
            provide: SwRegistrationOptions,
            useFactory: () => ({ enabled: environment.production }),
        },
        {
            provide: OKTA_CONFIG,
            useValue: oktaConfig
        },
        CurrencyPipe
    ]
    // bootstrap removed - using bootstrapApplication in main.ts instead
})
export class AppModule {
  constructor() {}
}
